<template>
    <product-listings-section
        class="menu-listings-section-module"
        :categories="categories"
        :listings-content="listingsContent"
        :selected-filters.sync="selectedFilters"
        :selected-sorts.sync="selectedSorts"
    />
</template>

<script>
    import ProductListingsSection from '../../../organisms/ProductListingsSection/ProductListingsSection';
    import { formatDietaryLabels } from '../../../../lib/dietary-labels';

    export default {
        name: 'menu-listings-section-module',

        components: { ProductListingsSection },

        props: {
            attributes: {
                type: Object,
                required: true,
                default: () => ({}),
            },
        },

        async asyncData(context) {
            const {
                store,
                query,
                onlyFetchProducts = false,
            } = context;

            const selectedFilters = Array.isArray(query.filters)
                ? query.filters
                : [query.filters].filter(Boolean);

            const filters = {};

            if (selectedFilters.length) {
                filters.and = selectedFilters.map((key) => ({
                    dietary_labels: {
                        key: {
                            eq: key,
                        },
                    },
                }));
            }

            const selectedSorts = Array.isArray(query.sort)
                ? query.sort
                : [query.sort].filter(Boolean);

            const category = context.attributes.product_category?.data?.id;

            if (category) {
                filters.or = [
                    {
                        category: {
                            id: {
                                eq: category,
                            },
                        },
                    },
                    {
                        categories: {
                            id: {
                                eq: category,
                            },
                        },
                    },
                ];
            }

            const data = await store.dispatch('products/fetchProducts', {
                filters,
                onlyFetchProducts,
            });

            return {
                selectedFilters,
                selectedSorts,
                ...data,
            };
        },

        data() {
            return {
                selectedFilters: [
                    ...this.attributes.asyncData.selectedFilters,
                ],
                selectedSorts: [
                    ...this.attributes.asyncData.selectedSorts,
                ],
                products: [
                    ...this.attributes.asyncData.products,
                ],
            };
        },

        computed: {
            /**
             * Return the categories for the sidebar with an "all" item if we're on a category already.
             *
             * @returns {{text: string, to: string}[]}
             */
            categories() {
                const categories = [
                    ...this.attributes.asyncData.categories,
                ];

                if (this.attributes.product_category?.data) {
                    categories.push(                    {
                        text: this.$snippet('view_all'),
                        to: this.attributes.menu_link
                            ?.data
                            ?.attributes
                            ?.uri,
                    });
                }

                return categories;
            },

            /**
             * Return the breadcrumbs to display on the page.
             *
             * @returns {{text: string, to: string}[]}
             */
            breadcrumbs() {
                const breadcrumbs = [
                    {
                        text: this.$snippet('home'),
                        to: '/',
                    },
                    {
                        text: this.$snippet('menu'),
                        to: this.attributes.menu_link
                            ?.data
                            ?.attributes
                            ?.uri,
                    },
                ];

                const category = this.attributes.product_category
                    ?.data;

                if (category) {
                    breadcrumbs.push({
                        text: category.attributes.name,
                        active: true,
                    });
                }

                return breadcrumbs;
            },

            /**
             * Return all the content to pass to the ProductListingsSection.
             *
             * @returns {object}
             */
            listingsContent() {
                return {
                    breadcrumbs: this.breadcrumbs,
                    filterTitle: this.$snippet('filter_by'),
                    sortTitle: this.$snippet('sort_by', 'sort by'),
                    filters: formatDietaryLabels(this, this.attributes.dietary_labels?.data),
                    sorts: formatDietaryLabels(this, this.attributes.sort_labels?.data),
                    items: this.products,
                    navItems: this.attributes.asyncData.menuLinks,
                    readMoreButtonText: this.attributes.read_more?.button_text,
                    readMoreContent: this.attributes.read_more?.content,
                };
            },
        },

        watch: {
            selectedFilters: 'fetchProducts',

            selectedSorts: 'fetchProducts',

            /**
             * Whenever the selected sorts change we want to sort the products and update the route.
             */
            selectedSorts() {
                this.sortProducts(this.products);
                this.updateRoute();
            },

            /**
             * Whenever the query updates we want to update the internal value and refetch.
             *
             * This catches instances where the page is navigated back and the query is changed/removed.
             *
             * @param {string|Array} val
             */
            '$route.query.filters'(val) {
                if (!Array.isArray(val)) {
                    val = [val].filter(Boolean);
                }
                // Only update if the value has changed
                if (JSON.stringify(val) !== JSON.stringify(this.selectedFilters)) {
                    this.selectedFilters = val;
                }
            },

            /**
             * Whenever the query updates we want to update the internal value and refetch.
             *
             * This catches instances where the page is navigated back and the query is changed/removed.
             *
             * @param {string} val
             */
            '$route.query.sort'(val) {
                if (!Array.isArray(val)) {
                    val = [val].filter(Boolean);
                }
                // Only update if the value has changed
                if (JSON.stringify(val) !== JSON.stringify(this.selectedSorts)) {
                    this.selectedSorts = val;
                }
            },

            /**
             * Whenever the query updates we want to update the internal value and refetch.
             *
             * This catches instances where the page is navigated back and the query is changed/removed.
             *
             * @param {string} val
             */
            '$route.query.sort'(val) {
                if (!Array.isArray(val)) {
                    val = [val].filter(Boolean);
                }
                this.selectedSorts = val;
            },
        },

        methods: {
            /**
             * Fetch the products from the api and update the internal values.
             */
            async fetchProducts() {
                const { products } = await this.$options.asyncData({
                    store: this.$store,
                    query: {
                        filters: this.selectedFilters,
                        sort: this.selectedSorts,
                    },
                    $config: this.$config,
                    attributes: this.attributes,
                    onlyFetchProducts: true,
                });

                this.sortProducts(products);

                this.updateRoute();

                this.products = products;
            },

            /**
             * Sort the products based on the selected sort.
             *
             * @param {object[]} products
             */
            sortProducts(products = []) {
                if (this.selectedSorts.length === 0) {
                    return;
                }
                const [ sortValue, direction ] = this.selectedSorts[0].split(':'); // sort will have :asc or :desc on the end
                products.sort((a, b) => {
                    const aValue = a[sortValue] || 0;
                    const bValue = b[sortValue] || 0;
                    const aIsGreaterThanB = parseFloat(aValue) > parseFloat(bValue);
                    if (direction === 'asc') {
                        return aIsGreaterThanB ? 1 : -1;
                    }
                    // default to desc
                    return aIsGreaterThanB ? -1 : 1;
                });
            },

            /**
             * Update the route with the selected filters and sorts.
             */
            updateRoute() {
                // Prevent duplicate navigation in tests
                if (
                    JSON.stringify(this.selectedFilters) !== JSON.stringify(this.$route.query.filters)
                    || JSON.stringify(this.selectedSorts) !== JSON.stringify(this.$route.query.sort)
                ) {
                    this.$router.push({
                        query: {
                            filters: this.selectedFilters,
                            sort: this.selectedSorts,
                        },
                    });
                }
            },
        },
    };
</script>
